import { getApiBaseUrl } from "../config/runtimeConfig";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

// RentNoticeDto (Módulo 5) — um aviso de renda de um contrato de arrendamento.
// camelCase pinado a partir do contrato do backend; não depende do web-api-client.
export interface RentNotice {
  id: number;
  rentalContractId: number;
  contractNumber: string | null;
  propertyId: number;
  propertyName: string;
  tenantId: number;
  tenantName: string;
  periodYear: number;
  periodMonth: number;
  periodLabel: string;
  amount: number;
  dueDate: string;
  issueDate: string;
  isPaid: boolean;
  paidAt: string | null;
  isOverdue: boolean;
  // Classificador do estado do aviso: "pendente" | "paga" | "atrasada".
  statusCode: string;
  notifiedByEmail: boolean;
  notifiedAt: string | null;
  notes: string | null;
}

// Resultado de POST /api/rent-notices/generate.
export interface GenerateRentNoticesResult {
  created: number;
  skipped: number;
  notices: RentNotice[];
}

// Filtros da lista — GET /api/rent-notices.
export interface RentNoticeFilters {
  year?: number;
  month?: number;
  propertyId?: number;
  rentalContractId?: number;
  statusCode?: string;
  onlyUnpaid?: boolean;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  return (await response.json()) as T;
}

export function getRentNotices(
  filters?: RentNoticeFilters,
): Promise<RentNotice[]> {
  const search = new URLSearchParams();
  if (filters?.year != null) {
    search.set("year", String(filters.year));
  }
  if (filters?.month != null) {
    search.set("month", String(filters.month));
  }
  if (filters?.propertyId != null) {
    search.set("propertyId", String(filters.propertyId));
  }
  if (filters?.rentalContractId != null) {
    search.set("rentalContractId", String(filters.rentalContractId));
  }
  if (filters?.statusCode && filters.statusCode.trim() !== "") {
    search.set("statusCode", filters.statusCode.trim());
  }
  if (filters?.onlyUnpaid) {
    search.set("onlyUnpaid", "true");
  }
  const query = search.toString();
  return request<RentNotice[]>(`/api/rent-notices${query ? `?${query}` : ""}`);
}

export function generateRentNotices(
  year: number,
  month: number,
): Promise<GenerateRentNoticesResult> {
  return request<GenerateRentNoticesResult>("/api/rent-notices/generate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ year, month }),
  });
}

export function setRentNoticePaid(
  id: number,
  isPaid: boolean,
  paidAt?: string,
): Promise<RentNotice> {
  return request<RentNotice>(`/api/rent-notices/${id}/paid`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ isPaid, paidAt }),
  });
}

export function sendRentNoticeEmail(id: number): Promise<RentNotice> {
  return request<RentNotice>(`/api/rent-notices/${id}/send-email`, {
    method: "POST",
  });
}

export async function deleteRentNotice(id: number): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/api/rent-notices/${id}`, {
    method: "DELETE",
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }
}
